import * as pdfjsLib from 'pdfjs-dist';

let workerReady = false;

function ensurePdfWorker() {
  if (workerReady) return;
  pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
  workerReady = true;
}

export type PdfProgressCallback = (percent: number) => void;

export async function pdfFirstPageToBlob(
  pdfBlob: Blob,
  mimeType: 'image/jpeg' | 'image/png' = 'image/jpeg',
  scale = 2,
): Promise<Blob> {
  ensurePdfWorker();
  const data = new Uint8Array(await pdfBlob.arrayBuffer());
  const pdf = await pdfjsLib.getDocument({ data }).promise;
  const page = await pdf.getPage(1);
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');

  await page.render({
    canvasContext: ctx,
    viewport,
    canvas,
    background: '#ffffff',
  }).promise;

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('PDF render failed'))),
      mimeType,
      mimeType === 'image/jpeg' ? 0.85 : undefined,
    );
  });
}

function loadImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image'));
    };
    img.src = url;
  });
}

function imageToCanvas(img: HTMLImageElement, scale = 1): HTMLCanvasElement {
  const width = Math.max(1, Math.round(img.naturalWidth * scale));
  const height = Math.max(1, Math.round(img.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);
  return canvas;
}

async function canvasToPdfBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  const { jsPDF } = await import('jspdf');
  const width = canvas.width;
  const height = canvas.height;
  const orientation = width >= height ? 'landscape' : 'portrait';
  const pdf = new jsPDF({ orientation, unit: 'px', format: [width, height] });
  pdf.addImage(canvas.toDataURL('image/jpeg', quality), 'JPEG', 0, 0, width, height);
  return pdf.output('blob');
}

async function buildPdfFromCanvases(canvases: HTMLCanvasElement[], quality: number): Promise<Blob> {
  const { jsPDF } = await import('jspdf');
  let doc: InstanceType<typeof jsPDF> | null = null;

  for (const canvas of canvases) {
    const w = canvas.width;
    const h = canvas.height;
    const orientation = w >= h ? 'landscape' : 'portrait';
    const dataUrl = canvas.toDataURL('image/jpeg', quality);

    if (!doc) {
      doc = new jsPDF({ orientation, unit: 'px', format: [w, h] });
      doc.addImage(dataUrl, 'JPEG', 0, 0, w, h);
    } else {
      doc.addPage([w, h], orientation);
      doc.addImage(dataUrl, 'JPEG', 0, 0, w, h);
    }
  }

  if (!doc) throw new Error('PDF has no pages');
  return doc.output('blob');
}

async function binarySearchPdfQuality(
  build: (quality: number) => Promise<Blob>,
  targetBytes: number,
  onProgress?: PdfProgressCallback,
): Promise<Blob> {
  let low = 0.22;
  let high = 0.9;
  let best = await build(high);
  onProgress?.(88);

  if (best.size <= targetBytes) return best;

  for (let i = 0; i < 7; i += 1) {
    const mid = (low + high) / 2;
    const attempt = await build(mid);
    onProgress?.(88 + Math.round(((i + 1) / 7) * 10));

    if (attempt.size <= targetBytes) {
      best = attempt;
      low = mid + 0.015;
    } else {
      high = mid - 0.015;
    }
  }

  return best;
}

export async function imageBlobToPdf(imageBlob: Blob): Promise<Blob> {
  const img = await loadImage(imageBlob);
  const canvas = imageToCanvas(img, 1);
  return canvasToPdfBlob(canvas, 0.88);
}

/** Re-encode a PDF so its file size is at or below targetBytes (all pages preserved). */
export async function pdfBlobToTargetSizePdf(
  pdfBlob: Blob,
  targetBytes: number,
  onProgress?: PdfProgressCallback,
): Promise<Blob> {
  if (pdfBlob.size <= targetBytes) return pdfBlob;

  ensurePdfWorker();
  onProgress?.(5);
  const data = new Uint8Array(await pdfBlob.arrayBuffer());
  const pdfDoc = await pdfjsLib.getDocument({ data }).promise;

  const sizeRatio = targetBytes / pdfBlob.size;
  let renderScale = Math.min(1.25, Math.max(0.35, Math.sqrt(sizeRatio) * 1.15));

  async function renderAllPages(scale: number): Promise<HTMLCanvasElement[]> {
    const canvases: HTMLCanvasElement[] = [];
    for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum += 1) {
      onProgress?.(5 + Math.round((pageNum / pdfDoc.numPages) * 45));
      const page = await pdfDoc.getPage(pageNum);
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas not supported');

      await page.render({
        canvasContext: ctx,
        viewport,
        canvas,
        background: '#ffffff',
      }).promise;

      canvases.push(canvas);
    }
    return canvases;
  }

  let canvases = await renderAllPages(renderScale);
  onProgress?.(55);

  let result = await binarySearchPdfQuality(
    (quality) => buildPdfFromCanvases(canvases, quality),
    targetBytes,
    onProgress,
  );

  if (result.size <= targetBytes) {
    onProgress?.(100);
    return result;
  }

  renderScale = Math.max(0.25, renderScale * 0.72);
  canvases = await renderAllPages(renderScale);
  onProgress?.(70);

  result = await binarySearchPdfQuality(
    (quality) => buildPdfFromCanvases(canvases, quality),
    targetBytes,
    onProgress,
  );

  if (result.size <= targetBytes * 1.08) {
    onProgress?.(100);
    return result;
  }

  throw new Error('Could not compress to the requested size. Try a larger KB value.');
}

export async function imageBlobToTargetSizePdf(
  imageBlob: Blob,
  targetBytes: number,
  onProgress?: PdfProgressCallback,
): Promise<Blob> {
  onProgress?.(10);
  const img = await loadImage(imageBlob);
  const sizeRatio = targetBytes / Math.max(imageBlob.size, 1);
  let scale = Math.min(1, Math.max(0.25, Math.sqrt(sizeRatio) * 1.1));

  let canvas = imageToCanvas(img, scale);
  onProgress?.(40);

  let result = await binarySearchPdfQuality(
    (quality) => canvasToPdfBlob(canvas, quality),
    targetBytes,
    onProgress,
  );

  if (result.size <= targetBytes) {
    onProgress?.(100);
    return result;
  }

  scale = Math.max(0.2, scale * 0.72);
  canvas = imageToCanvas(img, scale);
  onProgress?.(75);

  result = await binarySearchPdfQuality(
    (quality) => canvasToPdfBlob(canvas, quality),
    targetBytes,
    onProgress,
  );

  if (result.size <= targetBytes * 1.08) {
    onProgress?.(100);
    return result;
  }

  throw new Error('Could not compress to the requested size. Try a larger KB value.');
}

export async function filePreviewBase64(file: File): Promise<string | undefined> {
  const preview = await filePreviewForAi(file);
  return preview?.base64;
}

export async function filePreviewForAi(
  file: File,
): Promise<{ base64: string; mimeType: string } | undefined> {
  if (file.type.startsWith('image/')) {
    const buffer = await file.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    let binary = '';
    bytes.forEach((b) => { binary += String.fromCharCode(b); });
    return { base64: btoa(binary), mimeType: file.type || 'image/jpeg' };
  }
  if (file.type === 'application/pdf') {
    const pageBlob = await pdfFirstPageToBlob(file);
    const buffer = await pageBlob.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    let binary = '';
    bytes.forEach((b) => { binary += String.fromCharCode(b); });
    return { base64: btoa(binary), mimeType: 'image/jpeg' };
  }
  return undefined;
}
